import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';

import { OAuthApplicationsService } from '@generated/services/o-auth-applications.service';
import { OAuthApplicationDto } from '@generated/models';
import { NotificationService } from '@core/services/notification.service';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';

@Component({
  selector: 'app-oauth-applications',
  standalone: true,
  imports: [
    AssistantSurfaceDirective,
    AssistantActionDirective,
    FormsModule,
    ButtonModule,
    TableModule,
    TooltipModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    DeleteConfirmationComponent
],
  templateUrl: './oauth-applications.component.html',
  styleUrl: './oauth-applications.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OAuthApplicationsComponent implements OnInit {
  private oauthService = inject(OAuthApplicationsService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  applications = signal<OAuthApplicationDto[]>([]);
  filteredApplications = signal<OAuthApplicationDto[]>([]);
  nameFilter = '';
  loading = signal(false);

  deleteDialogVisible = false;
  isDeleting = false;
  appToDelete: OAuthApplicationDto | null = null;

  readonly deleteTitle = $localize`:@@oauthApps.delete.title:Delete application`;
  readonly deleteSubtitle = $localize`:@@oauthApps.delete.subtitle:Are you sure? This cannot be undone, the client will be deleted in Keycloak.`;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.oauthService.apiOAuthApplicationsGet().subscribe({
      next: (apps: OAuthApplicationDto[]) => {
        this.applications.set(apps || []);
        this.applyFilter();
        this.loading.set(false);
        this.cdr.markForCheck();
      },
      error: () => {
        this.notificationService.showError($localize`:@@oauthApps.notify.listFail:Failed to load applications`);
        this.loading.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  applyFilter(): void {
    const query = this.nameFilter.toLowerCase().trim();
    if (!query) {
      this.filteredApplications.set(this.applications());
    } else {
      this.filteredApplications.set(
        this.applications().filter(a => a.name?.toLowerCase().includes(query))
      );
    }
  }

  newApplication(): void {
    this.router.navigate(['/developers/oauth-applications/new']);
  }

  editApplication(app: OAuthApplicationDto): void {
    if (app.id) {
      this.router.navigate(['/developers/oauth-applications', app.id]);
    }
  }

  confirmDelete(app: OAuthApplicationDto, event: Event): void {
    event.stopPropagation();
    this.appToDelete = app;
    this.deleteDialogVisible = true;
    this.cdr.markForCheck();
  }

  onDeleteConfirm(): void {
    if (!this.appToDelete?.id) return;
    this.isDeleting = true;
    this.cdr.markForCheck();

    this.oauthService.apiOAuthApplicationsIdDelete({ id: this.appToDelete.id }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@oauthApps.notify.deleted:Application deleted`);
        this.isDeleting = false;
        this.deleteDialogVisible = false;
        this.appToDelete = null;
        this.load();
      },
      error: () => {
        this.notificationService.showError($localize`:@@oauthApps.notify.deleteFail:Failed to delete application`);
        this.isDeleting = false;
        this.deleteDialogVisible = false;
        this.cdr.markForCheck();
      }
    });
  }

  onDeleteCancel(): void {
    this.deleteDialogVisible = false;
    this.appToDelete = null;
    this.cdr.markForCheck();
  }

  async copyClientId(clientId: string | undefined, event: Event): Promise<void> {
    event.stopPropagation();
    if (!clientId) return;
    try {
      await navigator.clipboard.writeText(clientId);
      this.notificationService.showSuccess($localize`:@@oauthApps.notify.clientIdCopied:Client ID copied`);
    } catch {
      this.notificationService.showError($localize`:@@oauthApps.notify.copyFail:Failed to copy`);
    }
  }
}
